import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";
import { getAvatarInitials } from "../utils/employeeMapper";
import { invalidateEmployeeCache } from "../utils/employeeCache";

// CRUD actions: update / add / delete / archive / restore
export const useEmployeeActions = ({
  language,
  text,
  employees,
  setEmployees,
  archivedEmployees,
  setArchivedEmployees,
  archivedCount,
  setArchivedCount,
  selectedEmployee,
  setSelectedEmployee,
  setShowAddEmployee,
  setShowEditEmployee,
  setLoading,
  setError,
  setSuccess,
  fetchEmployeeCount,
  fetchEmployeeCounts,
}) => {
  // Function to handle updating employee data
  const handleUpdateEmployee = async (employeeData) => {
    try {
      // Set loading state
      setLoading(true);
      // Clear previous messages
      setError("");
      setSuccess("");

      // Prepare payload for API
      const payload = {
        sr: employeeData.name,
        email: employeeData.email,
        phoneNumber: employeeData.phone,
        departmentId: employeeData.departmentId ?? null,
        position: employeeData.position,
        laborMode: employeeData.laborMode,
        lastContractTerm: employeeData.lastContractTerm,
        clientName: employeeData.company,
        employeeState: employeeData.status,
        netSalary: employeeData.salary !== "" && employeeData.salary != null ? parseFloat(employeeData.salary) : null,
        grossSalary: employeeData.grossSalary !== "" && employeeData.grossSalary != null ? parseFloat(employeeData.grossSalary) : null,
        salaryInputSource: employeeData.salaryInputSource || null,
        nationalId: employeeData.nationalId || "",
        address: employeeData.address || "",
        city: employeeData.city || "",
        country: employeeData.country || "Egypt",
        nationality: employeeData.nationality || "",
        gender: employeeData.gender || "",
        maritalStatus: employeeData.maritalStatus || "",
        dob: employeeData.dateOfBirth
          ? new Date(employeeData.dateOfBirth).toISOString()
          : null,
      };

      // Make API call to update employee
      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployee.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const updated = await response.json();

      // Update employee in local state
      const updatedEmployees = employees.map(
        (emp) =>
          emp.id === selectedEmployee.id
            ? {
              // Spread existing employee data
              ...emp,
              // Update with new data from response
              name: language === "ar" ? (updated.arabicName || updated.sr) : (updated.sr || updated.arabicName),
              email: updated.email,
              phone: updated.phoneNumber,
              department: updated.department?.name ?? updated.employeeType,
              position: updated.position ?? emp.position,
              laborMode: updated.laborMode,
              lastContractTerm: updated.lastContractTerm,
              company: updated.clientName,
              status: updated.employeeState,
              salary: updated.netSalary,
              grossSalary: updated.grossSalary,
              // Update avatar with new name initials
              avatar: getAvatarInitials(updated.arabicName, updated.sr, language),
            }
            : emp // Return unchanged employee if not the one being updated
      );

      // Update employees state
      setEmployees(updatedEmployees);
      // The cached raw employee/hierarchy payload no longer reflects this
      // edit — drop it so the next fetch doesn't resurrect stale fields.
      invalidateEmployeeCache("manager-employees:");
      // Close edit modal
      setShowEditEmployee(false);
      // Clear selected employee
      setSelectedEmployee(null);
      // Show success message
      setSuccess(text[language].employeeUpdated);
    } catch (err) {
      // Log error and show error message
      console.error("Failed to update employee:", err);
      setError(err.response?.data?.error || text[language].failedToUpdate);
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle adding new employee
  const handleAddEmployee = async (employeeData) => {
    try {
      // Set loading state
      setLoading(true);
      // Clear previous messages
      setError("");
      setSuccess("");

      // Prepare payload for API
      const payload = {
        sr: employeeData.name,
        email: employeeData.email,
        phoneNumber: employeeData.phone,
        location: employeeData.address,
        position: employeeData.position,
        laborMode: employeeData.laborMode,
        lastContractTerm: employeeData.lastContractTerm,
        contractCompany: employeeData.company,
        clientCode: String(employeeData.clientCode ?? "").trim() || null,
        employeeState: employeeData.status,
        netSalary: employeeData.salary !== "" && employeeData.salary != null ? parseFloat(employeeData.salary) : null,
        grossSalary: employeeData.grossSalary !== "" && employeeData.grossSalary != null ? parseFloat(employeeData.grossSalary) : null,
        salaryInputSource: employeeData.salaryInputSource || null,
        hiringDate: new Date().toISOString(), // Set current date as hiring date
        nationalId: employeeData.nationalId || "",
        address: employeeData.address || "",
        city: employeeData.city || "",
        country: employeeData.country || "Egypt",
        nationality: employeeData.nationality || "",
        gender: employeeData.gender || "",
        maritalStatus: employeeData.maritalStatus || "",
        dob: employeeData.dateOfBirth
          ? new Date(employeeData.dateOfBirth).toISOString()
          : null,
      };

      // Make API call to add employee
      const response = await apiFetch(`${API_URL}/employees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const created = await response.json();
      // A rejected add (e.g. missing Client Code → 400) must not be turned
      // into a phantom row below.
      if (!response.ok || created?.success === false) {
        throw new Error(created?.error || created?.message || text[language].failedToAdd);
      }

      // Create new employee object from response
      const newEmployee = {
        id: created.infinityCode || created.id,
        name: created.sr,
        position: created.position ?? created.employeeType ?? "",
        department: created.department?.name ?? created.location ?? "",
        laborMode: created.laborMode,
        lastContractTerm: created.lastContractTerm,
        email: created.email,
        phone: created.phoneNumber,
        salary: created.netSalary,
        grossSalary: created.grossSalary,
        joinDate: created.hiringDate,
        status: created.employeeState,
        company: created.contractCompany,
        // Generate avatar initials
        avatar: created.sr
          ? created.sr[0].toUpperCase() +
          (created.sr.split(" ")[1]?.[0]?.toUpperCase() || "")
          : "NA",
      };

      // Add new employee to employees array
      setEmployees([...employees, newEmployee]);
      // The cached raw employee/hierarchy payload no longer includes this
      // new hire — drop it so the next fetch reflects reality.
      invalidateEmployeeCache("manager-employees:");
      // Close add modal
      setShowAddEmployee(false);
      // Show success message
      setSuccess(text[language].employeeAdded);

      // Refresh counts — force, since this mutation just changed the data.
      fetchEmployeeCount(true);
      fetchEmployeeCounts(true);
    } catch (err) {
      // Log error and show error message
      console.error("Failed to add employee:", err);
      setError(err.response?.data?.error || err.message || text[language].failedToAdd);
    } finally {
      // Set loading to false
      setLoading(false);
    }
  };

  // Function to handle deleting employee
  const handleDeleteEmployee = async (employeeId) => {
    // Show confirmation dialog
    if (window.confirm(text[language].confirmDelete)) {
      try {
        // Set loading state
        setLoading(true);
        // Make API call to delete employee
        await apiFetch(`${API_URL}/employees/${employeeId}`, { method: "DELETE" });

        // Remove employee from local state
        setEmployees(employees.filter((emp) => emp.id !== employeeId));
        // Drop the cached raw payload — it still contains the deleted employee.
        invalidateEmployeeCache("manager-employees:");
        // Show success message
        setSuccess(text[language].employeeDeleted);

        // Refresh counts
        fetchEmployeeCount(true);
        fetchEmployeeCounts(true);
      } catch (err) {
        // Log error and show error message
        console.error("Failed to delete employee:", err);
        setError(text[language].failedToDelete);
      } finally {
        // Set loading to false
        setLoading(false);
      }
    }
  };

  // Function to handle archiving employee
  const handleArchiveEmployee = async (employeeId) => {
    // Show confirmation dialog
    if (window.confirm(text[language].confirmArchive)) {
      try {
        // Set loading state
        setLoading(true);

        // Find the employee to archive
        const employeeToArchive = employees.find(
          (emp) => emp.id === employeeId
        );

        // Add archived date and update status
        const archivedEmployee = {
          ...employeeToArchive,
          archivedDate: new Date().toISOString(),
          status: "Archived",
        };

        // Send to archive endpoint
        await apiFetch(`${API_URL}/employees/archive`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          employeeId: employeeId,
          archivedData: archivedEmployee,
        });

        // Remove from active employees
        await apiFetch(`${API_URL}/employees/${employeeId}`, { method: "DELETE" });

        // Update local states
        setEmployees(employees.filter((emp) => emp.id !== employeeId));
        setArchivedEmployees([...archivedEmployees, archivedEmployee]);
        setArchivedCount(archivedCount + 1);
        // The cached raw payloads no longer match reality — drop both.
        invalidateEmployeeCache("manager-employees:");
        invalidateEmployeeCache("manager-archived-employees");
        setSuccess(text[language].employeeArchived);

        // Refresh counts
        fetchEmployeeCount(true);
        fetchEmployeeCounts(true);
      } catch (err) {
        // Log error and show error message
        console.error("Failed to archive employee:", err);
        setError(text[language].failedToArchive);
      } finally {
        // Set loading to false
        setLoading(false);
      }
    }
  };

  // Function to handle restoring archived employee
  const handleRestoreEmployee = async (employeeId) => {
    // Show confirmation dialog
    if (window.confirm(text[language].confirmRestore)) {
      try {
        // Set loading state
        setLoading(true);

        // Find the archived employee
        const employeeToRestore = archivedEmployees.find(
          (emp) => emp.id === employeeId
        );

        // Remove archived properties and set status to active
        const restoredEmployee = {
          ...employeeToRestore,
          status: "Active",
        };
        // Remove archived date property
        delete restoredEmployee.archivedDate;

        // Restore to active employees
        await apiFetch(`${API_URL}/employees/restore/${employeeId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          employeeId: employeeId,
          employeeData: restoredEmployee,
        });

        // Remove from archived
        await apiFetch(`${API_URL}/employees/archived/${employeeId}`, { method: "DELETE" });

        // Update local states
        setArchivedEmployees(
          archivedEmployees.filter((emp) => emp.id !== employeeId)
        );
        setEmployees([...employees, restoredEmployee]);
        setArchivedCount(archivedCount - 1);
        // The cached raw payloads no longer match reality — drop both.
        invalidateEmployeeCache("manager-employees:");
        invalidateEmployeeCache("manager-archived-employees");
        setSuccess(text[language].employeeRestored);

        // Refresh counts
        fetchEmployeeCount(true);
        fetchEmployeeCounts(true);
      } catch (err) {
        // Log error and show error message
        console.error("Failed to restore employee:", err);
        setError(text[language].failedToRestore);
      } finally {
        // Set loading to false
        setLoading(false);
      }
    }
  };

  return {
    handleUpdateEmployee,
    handleAddEmployee,
    handleDeleteEmployee,
    handleArchiveEmployee,
    handleRestoreEmployee,
  };
};
